import { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState, forwardRef } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';

import { Alert } from '@interstate/components/Alert';
import { Box } from '@interstate/components/Box';
import { Button } from '@interstate/components/Button';

import { featureToggleSelectors } from '@makemydeal/dr-shared-store';
import { DiscardChangesModal, LoadingSection, LoadingSectionParent } from '@makemydeal/dr-shared-components';
import * as editCustomerActionCreators from '@makemydeal/dr-dash-store/actions/editCustomerActionCreators';
import * as editCustomerSelectors from '@makemydeal/dr-dash-store/selectors/editCustomerSelectors';
import * as customerSpdSelectors from '@makemydeal/dr-dash-store/selectors/customerSpdSelectors';
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';
import * as offerSelectors from '@makemydeal/dr-dash-store/selectors/offerSelectors';

import CustomerCard, { type CustomerCardRef } from '../customerCard/CustomerCard';
import type { CustomerCardFormValues } from '../customerCard/utils/CustomerCardUtils';
import InsuranceFormContainer from '../vehicleInsurance/InsuranceFormContainer';
import type { InsuranceFormContainerRef } from '../vehicleInsurance/InsuranceFormContainer';
import {
    ASYNC_ERROR_BANNER_BODY,
    BUYER,
    CO_BUYER,
    BUYER_DETAILS,
    CO_BUYER_DETAILS,
    CONTRACTING_ALERT_BUYER_LABEL,
    CONTRACTING_ALERT_CO_BUYER_LABEL,
    CONTRACTING_ALERT_INSURANCE_LABEL,
    CONTRACTING_ALERT_PREFIX,
    CONTRACTING_ALERT_SUFFIX,
    CREATE_BUSINESS_ERROR_BODY,
    INSURANCE,
    VALIDATION_BANNER_BODY,
    VALIDATION_BANNER_TITLE
} from '../customerCard/constants';
import { CustomerTabs } from './CustomerTabs';
import type { CustomerTabsCustomerKey, CustomerTabsSubTabKey } from './CustomerTabs';

const DATE_PICKER_POPPER_SELECTOR = '.MuiPickersPopper-root';

const detailsSubTabFor = (customer: CustomerTabsCustomerKey): CustomerTabsSubTabKey =>
    customer === CO_BUYER ? CO_BUYER_DETAILS : BUYER_DETAILS;

export interface UnifiedCustomerCardRef {
    apply: () => void;
    cancel: () => void;
}

export interface UnifiedCustomerCardProps {
    showInlineActions?: boolean;
    onStateChange?: (state: { canApply: boolean; canCancel: boolean; isApplying: boolean }) => void;
    buyerBusinessConversionDraft?: CustomerCardFormValues | null;
    onCancelConversion?: () => void;
}

type TabKey = typeof BUYER | typeof CO_BUYER | typeof INSURANCE;

type ValidationState = Record<TabKey, { isValid: boolean }>;
type DirtyState = Record<TabKey, boolean>;

const useDetachedNode = (): HTMLDivElement => {
    const ref = useRef<HTMLDivElement | null>(null);
    if (ref.current === null) {
        ref.current = document.createElement('div');
    }
    return ref.current;
};

const hideOpenPopovers = (): void => {
    const poppers = document.querySelectorAll(DATE_PICKER_POPPER_SELECTOR);
    poppers.forEach((el) => {
        (el as HTMLElement).style.display = 'none';
    });
};

// Some tabs save instantly, others save by calling an API. This helper makes both look the same to the caller.
const resolveApply = (ref: CustomerCardRef | InsuranceFormContainerRef): { sync: boolean; result: Promise<unknown> } => {
    const returned: unknown = ref.apply();
    const sync = !(returned && typeof (returned as Promise<unknown>).then === 'function');
    return { sync, result: Promise.resolve(returned) };
};

const UnifiedCustomerCard = forwardRef<UnifiedCustomerCardRef, UnifiedCustomerCardProps>(
    ({ showInlineActions = false, onStateChange, buyerBusinessConversionDraft, onCancelConversion }, ref) => {
        const dispatch = useDispatch();
        const showEditCustomerSlideout = useSelector(editCustomerSelectors.showEditCustomerSlideout);
        const isSpdCheckLoading = useSelector(customerSpdSelectors.getCheckCustomerSpdLoading);
        const isGCDynamicValidationsEnabled = useSelector(featureToggleSelectors.isGCDynamicValidationsEnabled);
        const hasCoBuyer = useSelector(offerReduxSelectors.hasCoBuyer);
        const createBusinessLoading = useSelector(editCustomerSelectors.getCreateBusinessLoading);
        const createBusinessError = useSelector(editCustomerSelectors.getCreateBusinessError);
        const isBusinessShopper = useSelector(offerSelectors.getIsBusinessShopper);
        const isBusinessBuyer = isBusinessShopper || !!buyerBusinessConversionDraft;

        const initialCustomer: CustomerTabsCustomerKey = showEditCustomerSlideout === CO_BUYER && hasCoBuyer ? CO_BUYER : BUYER;
        const [activeCustomer, setActiveCustomer] = useState<CustomerTabsCustomerKey>(initialCustomer);
        const [activeSubTab, setActiveSubTab] = useState<CustomerTabsSubTabKey>(detailsSubTabFor(initialCustomer));

        useEffect(() => {
            if (!showEditCustomerSlideout) return;
            const next: CustomerTabsCustomerKey = showEditCustomerSlideout === CO_BUYER && hasCoBuyer ? CO_BUYER : BUYER;
            setActiveCustomer(next);
            setActiveSubTab(detailsSubTabFor(next));
            setAsyncApplyError(false);
        }, [showEditCustomerSlideout, hasCoBuyer]);

        useEffect(() => {
            if (buyerBusinessConversionDraft) {
                setActiveCustomer(BUYER);
                setActiveSubTab(BUYER_DETAILS);
            }
        }, [buyerBusinessConversionDraft]);

        const [validation, setValidation] = useState<ValidationState>({
            [BUYER]: { isValid: true },
            [CO_BUYER]: { isValid: true },
            [INSURANCE]: { isValid: true }
        });
        const [dirty, setDirty] = useState<DirtyState>({
            [BUYER]: false,
            [CO_BUYER]: false,
            [INSURANCE]: false
        });

        const [isApplying, setIsApplying] = useState(false);
        const [asyncApplyError, setAsyncApplyError] = useState(false);
        const [showDiscardModal, setShowDiscardModal] = useState(false);

        const [hasContractErrors, setHasContractErrors] = useState<Partial<Record<TabKey, boolean>>>({});

        const handleContractingErrorsAlert = useCallback(
            (entity: TabKey, hasErrors: boolean) =>
                setHasContractErrors((prev) => (prev[entity] === hasErrors ? prev : { ...prev, [entity]: hasErrors })),
            []
        );

        const buyerCardRef = useRef<CustomerCardRef>(null);
        const coBuyerCardRef = useRef<CustomerCardRef>(null);
        const insuranceFormRef = useRef<InsuranceFormContainerRef>(null);
        const buyerHost = useDetachedNode();
        const coBuyerHost = useDetachedNode();
        const insuranceHost = useDetachedNode();

        const hostByTab: Record<TabKey, HTMLDivElement> = {
            [BUYER]: buyerHost,
            [CO_BUYER]: coBuyerHost,
            [INSURANCE]: insuranceHost
        };

        const activeTabKey: TabKey = useMemo(() => {
            if (activeSubTab === INSURANCE) return INSURANCE;
            return activeCustomer === CO_BUYER ? CO_BUYER : BUYER;
        }, [activeCustomer, activeSubTab]);

        const activeHost = hostByTab[activeTabKey];

        const anyTabDirty = dirty[BUYER] || dirty[CO_BUYER] || dirty[INSURANCE];

        const anyDirtyTabInvalid =
            (dirty[BUYER] && !validation[BUYER].isValid) ||
            (dirty[CO_BUYER] && !validation[CO_BUYER].isValid) ||
            (dirty[INSURANCE] && !validation[INSURANCE].isValid);

        const activeIsInvalid = !validation[activeTabKey].isValid;
        const activeContractingHasErrors = hasContractErrors[activeTabKey];
        const showContractingAlert = isGCDynamicValidationsEnabled && activeContractingHasErrors && !isSpdCheckLoading;

        const closeCustomerSlideOut = useCallback(() => {
            dispatch(editCustomerActionCreators.hideEditCustomerSlideout());
        }, [dispatch]);

        const onApplyClick = useCallback(() => {
            setAsyncApplyError(false);
            const candidates: Array<{ key: TabKey; ref: CustomerCardRef | InsuranceFormContainerRef }> = [];
            if (dirty[BUYER] && buyerCardRef.current) candidates.push({ key: BUYER, ref: buyerCardRef.current });
            if (dirty[CO_BUYER] && hasCoBuyer && coBuyerCardRef.current)
                candidates.push({ key: CO_BUYER, ref: coBuyerCardRef.current });
            if (dirty[INSURANCE] && insuranceFormRef.current) candidates.push({ key: INSURANCE, ref: insuranceFormRef.current });

            const resolved = candidates.map(({ key, ref }) => ({ key, ...resolveApply(ref) }));
            const allSync = resolved.every((r) => r.sync);

            if (allSync) {
                closeCustomerSlideOut();
                return;
            }

            setIsApplying(true);
            Promise.allSettled(resolved.map((r) => r.result)).then((results) => {
                // Failure = promise rejected OR child resolved to explicit `false` (e.g. InsuranceForm API failure).
                const anyFailed = results.some((r) => r.status === 'rejected' || (r.status === 'fulfilled' && r.value === false));
                if (anyFailed) {
                    setIsApplying(false);
                    setAsyncApplyError(true);
                    return;
                }
                setIsApplying(false);
                onCancelConversion?.();
                closeCustomerSlideOut();
            });
        }, [dirty, hasCoBuyer, closeCustomerSlideOut, onCancelConversion]);

        const onCancelClick = useCallback(() => {
            if (isApplying) return;
            if (!anyTabDirty) {
                if (buyerBusinessConversionDraft && onCancelConversion) {
                    onCancelConversion();
                } else {
                    closeCustomerSlideOut();
                }
                return;
            }
            setShowDiscardModal(true);
        }, [isApplying, anyTabDirty, closeCustomerSlideOut, buyerBusinessConversionDraft, onCancelConversion]);

        // Expose apply and cancel methods to parent via ref
        useImperativeHandle(
            ref,
            () => ({
                apply: onApplyClick,
                cancel: onCancelClick
            }),
            [onApplyClick, onCancelClick]
        );

        const applyDisabled = !anyTabDirty || anyDirtyTabInvalid || isApplying || createBusinessLoading;
        const cancelDisabled = isApplying || createBusinessLoading;

        // Push canApply/canCancel/isApplying state to parent
        useEffect(() => {
            if (onStateChange) {
                onStateChange({
                    canApply: !applyDisabled,
                    canCancel: !cancelDisabled,
                    isApplying: isApplying || createBusinessLoading
                });
            }
        }, [onStateChange, applyDisabled, cancelDisabled, isApplying, createBusinessLoading]);

        const visibleMountRef = useCallback(
            (node: HTMLDivElement | null) => {
                if (node) {
                    node.replaceChildren(activeHost);
                    if (buyerBusinessConversionDraft) {
                        const businessNameInput = activeHost.querySelector(
                            'input[name="business-name"]'
                        ) as HTMLInputElement | null;
                        businessNameInput?.focus?.();
                    }
                }
            },
            [activeHost, buyerBusinessConversionDraft]
        );

        const handleCustomerChange = useCallback(
            (next: CustomerTabsCustomerKey) => {
                if (isApplying) return;
                hideOpenPopovers();
                setActiveCustomer(next);
                setActiveSubTab(detailsSubTabFor(next));
            },
            [isApplying]
        );

        const handleSubTabActivate = useCallback(
            (next: CustomerTabsSubTabKey) => {
                if (isApplying) return;
                hideOpenPopovers();
                setActiveSubTab(next);
            },
            [isApplying]
        );

        const handleTabValidationChange = useCallback((tab: TabKey, isValid: boolean, formChanged: boolean) => {
            setValidation((prev) => (prev[tab].isValid === isValid ? prev : { ...prev, [tab]: { isValid } }));
            setDirty((prev) => (prev[tab] === formChanged ? prev : { ...prev, [tab]: formChanged }));
        }, []);

        const handleDiscardConfirm = useCallback(() => {
            setShowDiscardModal(false);
            if (buyerBusinessConversionDraft && onCancelConversion) {
                onCancelConversion();
            } else {
                closeCustomerSlideOut();
            }
        }, [closeCustomerSlideOut, buyerBusinessConversionDraft, onCancelConversion]);

        const handleDiscardGoBack = useCallback(() => {
            setShowDiscardModal(false);
        }, []);

        const buyerCardElement = (
            <CustomerCard
                customer={BUYER}
                ref={buyerCardRef}
                hideApply={true}
                hideCancel={true}
                onValidationChange={(isValid, formChanged) => handleTabValidationChange(BUYER, isValid, formChanged)}
                disableDiscardModal={true}
                enableSpd={true}
                buyerBusinessConversionDraft={buyerBusinessConversionDraft}
                onContractingErrorsAlert={(hasErrors) => handleContractingErrorsAlert(BUYER, hasErrors)}
            />
        );

        const coBuyerCardElement = hasCoBuyer ? (
            <CustomerCard
                customer={CO_BUYER}
                ref={coBuyerCardRef}
                hideApply={true}
                hideCancel={true}
                onValidationChange={(isValid, formChanged) => handleTabValidationChange(CO_BUYER, isValid, formChanged)}
                disableDiscardModal={true}
                enableSpd={true}
                onContractingErrorsAlert={(hasErrors) => handleContractingErrorsAlert(CO_BUYER, hasErrors)}
            />
        ) : null;

        const insuranceElement = (
            <InsuranceFormContainer
                ref={insuranceFormRef}
                embedded
                onValidationChange={(isValid, formChanged) => handleTabValidationChange(INSURANCE, isValid, formChanged)}
                onContractingErrorsAlert={(hasErrors) => handleContractingErrorsAlert(INSURANCE, hasErrors)}
            />
        );

        const getContractingAlertLabel = (): string => {
            switch (activeTabKey) {
                case INSURANCE:
                    return CONTRACTING_ALERT_INSURANCE_LABEL;
                case CO_BUYER:
                    return CONTRACTING_ALERT_CO_BUYER_LABEL;
                default:
                    return CONTRACTING_ALERT_BUYER_LABEL;
            }
        };

        // Banner priority (highest first): contracting errors → validation errors → async apply failure → create-business failure.
        const getBannerContent = (): { title?: string; body: string } | null => {
            switch (true) {
                case showContractingAlert:
                    return { body: `${CONTRACTING_ALERT_PREFIX}${getContractingAlertLabel()}${CONTRACTING_ALERT_SUFFIX}` };
                case activeIsInvalid:
                    return { title: VALIDATION_BANNER_TITLE, body: VALIDATION_BANNER_BODY };
                case asyncApplyError:
                    return { body: ASYNC_ERROR_BANNER_BODY };
                case !!createBusinessError:
                    return { body: CREATE_BUSINESS_ERROR_BODY };
                default:
                    return null;
            }
        };
        const bannerContent = getBannerContent();

        const banner = bannerContent ? (
            <Alert
                type="error"
                data-testid="unified-alert-banner"
                title={bannerContent.title}
                sx={{ marginTop: '1rem', marginBottom: '2rem' }}
            >
                {bannerContent.body}
            </Alert>
        ) : null;

        const tabBodyContent = (
            <LoadingSectionParent>
                <LoadingSection loading={isApplying || createBusinessLoading} />
                {banner}
                <div data-testid="unified-edit-customer-mount" ref={visibleMountRef} />
            </LoadingSectionParent>
        );

        const body = (
            <>
                {createPortal(buyerCardElement, buyerHost)}
                {coBuyerCardElement && createPortal(coBuyerCardElement, coBuyerHost)}
                {createPortal(insuranceElement, insuranceHost)}
                <CustomerTabs
                    activeCustomer={activeCustomer}
                    activeSubTab={activeSubTab}
                    onCustomerChange={handleCustomerChange}
                    onSubTabActivate={handleSubTabActivate}
                    isBusinessBuyer={isBusinessBuyer}
                    errorEntities={hasContractErrors}
                />
                {tabBodyContent}
            </>
        );

        const discardModal = (
            <DiscardChangesModal
                show={showDiscardModal}
                onDiscard={handleDiscardConfirm}
                onGoBack={handleDiscardGoBack}
                showSimpleDiscard={!anyDirtyTabInvalid}
                hasValidationErrors={anyDirtyTabInvalid}
            />
        );

        if (showInlineActions) {
            return (
                <>
                    <div data-testid="unified-edit-customer-inline">
                        {body}
                        {!isSpdCheckLoading && (
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', padding: '1rem 0' }}>
                                <Button
                                    data-testid="customer-slideout-cancel-button"
                                    buttonStyle="tertiary"
                                    disabled={cancelDisabled}
                                    onClick={onCancelClick}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    data-testid="customer-slideout-apply-button"
                                    disabled={applyDisabled}
                                    onClick={onApplyClick}
                                >
                                    Apply
                                </Button>
                            </Box>
                        )}
                    </div>
                    {discardModal}
                </>
            );
        }

        return (
            <>
                {body}
                {discardModal}
            </>
        );
    }
);

UnifiedCustomerCard.displayName = 'UnifiedCustomerCard';

export default UnifiedCustomerCard;
